import { useEffect, useState } from "react";
import { notify } from "../../common/Toast";
import {
  foundry,
  materialInward,
  rawMaterials,
  SpectroCom,
} from "../../utlis/apiService";
import { HiMinusCircle } from "react-icons/hi";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";
import Model from "../../common/Model";
import EntityAddModal from "../../common/EntityAddModal";
import { motion, AnimatePresence } from "framer-motion";
import { IoMdClose } from "react-icons/io";
import { BsSave2 } from "react-icons/bs";
import Button from "../../common/Button";
import InputField from "../../common/InputField";
import { IoClose } from "react-icons/io5";
import { FaPaperPlane } from "react-icons/fa";

dayjs.extend(duration);
dayjs.extend(utc);

const SectionTitle = ({ title, className }) => {
  const defaultClass =
    "text-lg font-semibold text-sky-700 mb-3 pt-4 border-t border-slate-300 first:border-t-0 first:pt-0 uppercase";

  return <h3 className={className || defaultClass}>{title}</h3>;
};

const fixedSelectComponents = [
  { label: "Mg", value: 1 },
  { label: "Si", value: 2 },
  { label: "Fe", value: 3 },
  { label: "Zn", value: 4 },
  { label: "Cu", value: 5 },
  { label: "Al", value: 6 },
];

const FoundryForm = ({ onCancel, initialData, onSave, isEditing }) => {
  const today = new Date().toISOString()?.split("T")[0];
  const [isRawMaterialData, setIsRawMaterialData] = useState([]);
  const [isSkuData, setIsSkuData] = useState([]);
  const [isSpectroData, setIsSpectroData] = useState([]);
  const [scrapInputs, setScrapInputs] = useState([
    { Inward_No: "", qty: "", casting_No: "" },
  ]);
  const [sampleSpectroInputs, setSampleSpectroInputs] = useState(
    fixedSelectComponents.map((c) => ({
      componentId: c.value,
      label: c.label,
      percentage: "",
      editable: false,
    }))
  );

  const [finalSpectroInputs, setFinalSpectroInputs] = useState(
    fixedSelectComponents.map((c) => ({
      componentId: c.value,
      label: c.label,
      percentage: "",
      editable: false,
    }))
  );

  const [otherMetalEntries, setOtherMetalEntries] = useState([
    {
      CASTING_NO: "",
      INGOT: 0,
      PLANT_SCRAP: 0,
      PLANT_DISCARD: 0,
      DROSS_INGOT: 0,
      BEND_LOG: 0,
      HILL_METAL: 0,
      PIT_METAL: 0,
      TENDIS_METAL: 0,
      DROSS_METAL: 0,
      CREATED_BY: 1,
    },
  ]);
  const [foundryEntry, setFoundryEntry] = useState({
    CASTING_DATE: today,
    IN_CHARGE_ID: "",
    LDO_PRE_HEATING_LEVEL: "",
    LDO_CHARGING_START_LEVEL: "",
    LDO_CATING_TIME_LEVEL: "",
    LDO_MAIN_TANK_OPENING_LEVEL: "",
    LDO_MAIN_TANK_CLOSING_LEVEL: "",
    LDO_TOTAL_CONSUMPTION: "",
    ALLOY_GRAD: "",
    HLINE_GRAD: "",
    NO_OF_LOGS: "",
    LOG_LENGTH: "",
    LOG_WEIGHT: "",
    TOTAL_OUTPUT_WEIGHT: "",
    REMARK: "",
    CREATED_BY: 1,
  });
  const [furnaceEntries, setFurnaceEntries] = useState([
    {
      FURNACE_NO: 1,
      PRE_HEATED: false,
      CHARGING_START_TIME: "",
      CHARGING_END_TIME: "",
      CASTING_START_TIME: "",
      CASTING_END_TIME: "",
      TOTAL_TIME: "",
      CREATED_BY: 1,
    },
  ]);
  const [byProductEntries, setByProductEntries] = useState([
    {
      DROSS: 0,
      ALUMINIUM_METAL_REUSABLE: 0,
      IRON: 0,
      DROSS_INGOT: 0,
      BURNING_LOSS: 0,
      CREATED_BY: 1,
    },
  ]);
  const [chemicalEntries, setChemicalEntries] = useState([
    { MATERIAL_ID: "", QUANTITY: "", CREATED_BY: 1 },
  ]);
  const [rawMaterialsResData, setRawMaterialsResData] = useState([]);
  const [scrapTotal, setScrapTotal] = useState(0);
  const [totalInputKg, setTotalInputKg] = useState(0);
  const [modalType, setModalType] = useState(null);

  useEffect(() => {
    if (!initialData) {
      const now = dayjs().format("YYYY-MM-DDTHH:mm");
      setFurnaceEntries((prev) =>
        prev.map((entry) => ({
          ...entry,
          CHARGING_START_TIME: now,
          CASTING_END_TIME: now,
          CASTING_START_TIME: now,
          CHARGING_END_TIME: now,
        }))
      );
    }
  }, []);

  const handleScrapChange = (index, field, value) => {
    const updated = [...scrapInputs];
    updated[index][field] = value;

    if (field === "Inward_No") {
      const selected = isSkuData.find((opt) => opt.value === Number(value));
      updated[index].skuDetail = selected?.detail || "";
    }

    setScrapInputs(updated);

    const newScrapTotal = updated.reduce(
      (sum, scrap) => sum + (parseFloat(scrap.QUANTITY) || 0),
      0
    );
    setScrapTotal(newScrapTotal);
  };

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [skuRes, rawMaterialsRes, spectroRes] = await Promise.all([
          materialInward.getAllSKU(),
          rawMaterials.getAll(),
          SpectroCom.getAll(),
        ]);

        setIsSkuData(
          skuRes.map((c) => ({
            value: c.inward_No,
            label: c.skU_No,
            detail: c.skU_Detail,
          }))
        );
        setRawMaterialsResData(rawMaterialsRes);
        setIsRawMaterialData(
          rawMaterialsRes.map((c) => ({
            value: Number(c.id),
            label: c.name,
          }))
        );

        setIsSpectroData(
          spectroRes.map((c) => ({
            value: c.components_Id,
            label: c.components_Name,
          }))
        );
      } catch (error) {
        notify("Error fetching dropdown data", "error");
      }
    };
    fetchDropdownData();
  }, []);

  const handleAddScrapInput = () => {
    setScrapInputs([...scrapInputs, { Inward_No: "", qty: "" }]);
  };

  const handleAddChemicalInput = () => {
    setChemicalEntries([
      ...chemicalEntries,
      { materialId: "", qty: "", stock: "" },
    ]);
  };

  const handleAddSampleSpectro = () => {
    setSampleSpectroInputs([
      ...sampleSpectroInputs,
      { componentId: "", percentage: "" },
    ]);
  };

  const handleAddFinalSpectro = () => {
    setFinalSpectroInputs([
      ...finalSpectroInputs,
      { componentId: "", percentage: "" },
    ]);
  };

  const handleRemoveScrapInput = async (indexToRemove) => {
    const item = scrapInputs[indexToRemove];
    if (isEditing && item.SEQ_NO) {
      try {
        await foundry.deleteScrap(foundryEntry.CASTING_NO, item.SEQ_NO);
        notify("Scrap entry deleted", "success");
      } catch (err) {
        notify("Failed to delete scrap entry", "error");
        return;
      }
    }
    setScrapInputs(scrapInputs.filter((_, idx) => idx !== indexToRemove));
  };

  const handleRemoveChemicals = async (indexToRemove) => {
    const item = chemicalEntries[indexToRemove];

    if (isEditing && item.SEQ_NO) {
      try {
        await foundry.deleteChemical(foundryEntry.CASTING_NO, item.SEQ_NO);
        notify("Chemical entry deleted", "success");
      } catch (err) {
        notify("Failed to delete chemical entry", "error");
        return;
      }
    }

    setChemicalEntries(
      chemicalEntries.filter((_, idx) => idx !== indexToRemove)
    );
  };

  const handleRemoveSampleSpectro = async (indexToRemove) => {
    const item = sampleSpectroInputs[indexToRemove];

    if (isEditing && item.SEQ_NO) {
      try {
        await foundry.deleteSampleSpectro(foundryEntry.CASTING_NO, item.SEQ_NO);
        notify("Sample Spectro deleted", "success");
      } catch (err) {
        notify("Failed to delete Sample Spectro", "error");
        return;
      }
    }

    setSampleSpectroInputs(
      sampleSpectroInputs.filter((_, idx) => idx !== indexToRemove)
    );
  };

  const handleRemoveFinalSpectro = async (indexToRemove) => {
    const item = finalSpectroInputs[indexToRemove];

    if (isEditing && item.SEQ_NO) {
      try {
        await foundry.deleteFinalSpectro(foundryEntry.CASTING_NO, item.SEQ_NO);
        notify("Final Spectro deleted", "success");
      } catch (err) {
        notify("Failed to delete Final Spectro", "error");
        return;
      }
    }

    setFinalSpectroInputs(
      finalSpectroInputs.filter((_, idx) => idx !== indexToRemove)
    );
  };

  const handleFurnaceChange = (index, field, value) => {
    const updated = [...furnaceEntries];
    updated[index][field] = value;

    const { CHARGING_START_TIME, CASTING_END_TIME } = updated[index];
    if (CHARGING_START_TIME && CASTING_END_TIME) {
      const start = dayjs(CHARGING_START_TIME);
      const end = dayjs(CASTING_END_TIME);

      if (end.isBefore(start)) {
        updated[index].TOTAL_TIME = null;
      } else {
        const duration = dayjs.duration(end.diff(start));
        const totalHours = duration.asHours();
        const hours = Math.floor(totalHours);
        const minutes = duration.minutes();
        const seconds = duration.seconds();

        updated[index].TOTAL_TIME = `${String(hours).padStart(2, "0")}:${String(
          minutes
        ).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
      }
    } else {
      updated[index].TOTAL_TIME = null;
    }

    setFurnaceEntries(updated);
  };

  const totalOtherMetalsKg = otherMetalEntries.reduce((sum, item) => {
    return (
      sum +
      Number(item.INGOT || 0) +
      Number(item.PLANT_SCRAP || 0) +
      Number(item.PLANT_DISCARD || 0) +
      Number(item.DROSS_INGOT || 0) +
      Number(item.BEND_LOG || 0) +
      Number(item.HILL_METAL || 0) +
      Number(item.PIT_METAL || 0) +
      Number(item.TENDIS_METAL || 0) +
      Number(item.DROSS_METAL || 0)
    );
  }, 0);

  const handleOtherMetalChange = (index, field, value) => {
    const updated = [...otherMetalEntries];
    updated[index][field] = value;

    const calcWeight = (item) =>
      Number(item.INGOT || 0) +
      Number(item.PLANT_SCRAP || 0) +
      Number(item.PLANT_DISCARD || 0) +
      Number(item.DROSS_INGOT || 0) +
      Number(item.BEND_LOG || 0) +
      Number(item.HILL_METAL || 0) +
      Number(item.PIT_METAL || 0) +
      Number(item.TENDIS_METAL || 0) +
      Number(item.DROSS_METAL || 0);

    updated[index].TOTAL_WEIGHT = calcWeight(updated[index]);

    setOtherMetalEntries(updated);

    const allMetalsSum = updated.reduce(
      (sum, item) => sum + (item.TOTAL_WEIGHT || 0),
      0
    );
    setTotalInputKg(scrapTotal + allMetalsSum);
  };

  useEffect(() => {
    const metalsSum = otherMetalEntries.reduce((sum, item) => {
      return (
        sum +
        Number(item.INGOT || 0) +
        Number(item.PLANT_SCRAP || 0) +
        Number(item.PLANT_DISCARD || 0) +
        Number(item.DROSS_INGOT || 0) +
        Number(item.BEND_LOG || 0) +
        Number(item.HILL_METAL || 0) +
        Number(item.PIT_METAL || 0) +
        Number(item.TENDIS_METAL || 0) +
        Number(item.DROSS_METAL || 0)
      );
    }, 0);

    setTotalInputKg(scrapTotal + metalsSum);
  }, [scrapTotal, otherMetalEntries]);

  useEffect(() => {
    if (initialData) {
      setFoundryEntry(initialData.foundryEntry || {});
      setScrapInputs(initialData.scrapEntries || []);
      setScrapTotal(initialData.scrapTotal || 0);
      setOtherMetalEntries(initialData.otherMetalEntries || []);
      setFurnaceEntries(initialData.furnaceEntries || []);
      setByProductEntries(initialData.byProductEntries || []);
      setChemicalEntries(initialData.chemicalEntries || []);
      setFinalSpectroInputs(initialData.finalSpectroEntries || []);
      setSampleSpectroInputs(initialData.sampleSpectroEntries || []);
    }
  }, [initialData]);

  useEffect(() => {
    const logLength = Number(foundryEntry.LOG_LENGTH || 0);
    const logCount = Number(foundryEntry.NO_OF_LOGS || 0);
    const singleWeight = logLength * 0.034;
    const totalWeight = singleWeight * logCount;

    setFoundryEntry((prev) => ({
      ...prev,
      LOG_WEIGHT: parseFloat(singleWeight.toFixed(2)),
      TOTAL_OUTPUT_WEIGHT: parseFloat(totalWeight.toFixed(2)),
    }));
  }, [foundryEntry.LOG_LENGTH, foundryEntry.NO_OF_LOGS]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // const isValid = validateForm();
    // if (!isValid) return;

    const payload = {
      foundryEntry: {
        ...foundryEntry,
        IN_CHARGE_ID: Number(foundryEntry.IN_CHARGE_ID),
        LDO_PRE_HEATING_LEVEL: Number(foundryEntry.LDO_PRE_HEATING_LEVEL),
        LDO_CHARGING_START_LEVEL: Number(foundryEntry.LDO_CHARGING_START_LEVEL),
        LDO_CATING_TIME_LEVEL: Number(foundryEntry.LDO_CATING_TIME_LEVEL),
        LDO_MAIN_TANK_OPENING_LEVEL: Number(
          foundryEntry.LDO_MAIN_TANK_OPENING_LEVEL
        ),
        LDO_MAIN_TANK_CLOSING_LEVEL: Number(
          foundryEntry.LDO_MAIN_TANK_CLOSING_LEVEL
        ),
        LDO_TOTAL_CONSUMPTION: Number(foundryEntry.LDO_TOTAL_CONSUMPTION),
        NO_OF_LOGS: Number(foundryEntry.NO_OF_LOGS),
        LOG_LENGTH: Number(foundryEntry.LOG_LENGTH),
        LOG_WEIGHT: Number(foundryEntry.LOG_WEIGHT),
        TOTAL_OUTPUT_WEIGHT: Number(foundryEntry.TOTAL_OUTPUT_WEIGHT),
        CREATED_BY: 1,
      },
      scrapEntries: scrapInputs.map((item, index) => ({
        SEQ_NO: index + 1,
        CASTING_NO: isEditing ? initialData?.CASTING_NO || "" : "",
        INWARD_NO: Number(item.Inward_No),
        QUANTITY: Number(item.QUANTITY),
        CREATED_BY: 1,
      })),
      otherMetalEntries: otherMetalEntries.map((item) => ({
        ...item,
        INGOT: Number(item.INGOT),
        PLANT_SCRAP: Number(item.PLANT_SCRAP),
        PLANT_DISCARD: Number(item.PLANT_DISCARD),
        DROSS_INGOT: Number(item.DROSS_INGOT),
        BEND_LOG: Number(item.BEND_LOG),
        HILL_METAL: Number(item.HILL_METAL),
        PIT_METAL: Number(item.PIT_METAL),
        TENDIS_METAL: Number(item.TENDIS_METAL),
        DROSS_METAL: Number(item.DROSS_METAL),
        TOTAL_WEIGHT: Number(totalOtherMetalsKg),
        CREATED_BY: 1,
      })),
      furnaceEntries: furnaceEntries.map((item) => ({
        FURNACE_NO: Number(item.FURNACE_NO),
        PRE_HEATED: Boolean(item.PRE_HEATED),
        CHARGING_START_TIME: item.CHARGING_START_TIME,
        CHARGING_END_TIME: item.CHARGING_END_TIME,
        CASTING_START_TIME: item.CASTING_START_TIME,
        CASTING_END_TIME: item.CASTING_END_TIME,
        TOTAL_TIME: item.TOTAL_TIME,
        CREATED_BY: 1,
      })),
      byProductEntries: byProductEntries.map((item) => ({
        ...item,
        DROSS: Number(item.DROSS),
        ALUMINIUM_METAL_REUSABLE: Number(item.ALUMINIUM_METAL_REUSABLE),
        IRON: Number(item.IRON),
        DROSS_INGOT: Number(item.DROSS_INGOT),
        BURNING_LOSS: Number(item.BURNING_LOSS),
        CREATED_BY: 1,
      })),
      chemicalEntries: chemicalEntries.map((item, index) => ({
        SEQ_NO: index + 1,
        MATERIAL_ID: Number(item.MATERIAL_ID),
        QUANTITY: Number(item.qty),
        CREATED_BY: 1,
      })),
      finalSpectroEntries: finalSpectroInputs.map((item, index) => ({
        Seq_No: index + 1,
        Components_Id: Number(item.componentId),
        Components_Value: Number(item.percentage),
        Created_By: 1,
      })),
      sampleSpectroEntries: sampleSpectroInputs.map((item, index) => ({
        Seq_No: index + 1,
        Components_Id: Number(item.componentId),
        Components_Value: Number(item.percentage),
        Created_By: 1,
      })),
    };

    onSave(payload, isEditing ? initialData?.CASTING_NO : "");
  };

  const optionMap = {
    rawMaterial: {
      setter: setIsRawMaterialData,
      field: "id",
    },
    sampleSpectroComponent: {
      setter: setIsSpectroData,
      field: "components_Id",
    },
    finalSpectroComponent: {
      setter: setIsSpectroData,
      field: "components_Id",
    },
  };

  const handleEntityAddSuccess = async (type) => {
    const { setter } = optionMap[type];

    try {
      let updatedData;

      switch (type) {
        case "rawMaterial":
          updatedData = await rawMaterials.getAll();
          setter(
            updatedData.map((c) => ({
              value: c.id,
              label: c.name,
            }))
          );
          break;
        case "sampleSpectroComponent":
          updatedData = await SpectroCom.getAll();
          setter(
            updatedData.map((s) => ({
              value: s.components_Id,
              label: s.components_Name,
            }))
          );
          break;
        case "finalSpectroComponent":
          updatedData = await SpectroCom.getAll();
          setter(
            updatedData.map((s) => ({
              value: s.components_Id,
              label: s.components_Name,
            }))
          );
          break;
        default:
          return;
      }

      setModalType(null);
    } catch (err) {
      notify("Failed to refresh dropdown", "error");
    }
  };
  const isModalOpen = true;

  const tabs = [
    "Scrap Inputs",
    "LDO Table",
    "Other Metals",
    "Furnace Time",
    "Consumables",
    "By-Products",
    "Log Output",
    "Spectro Analysis",
  ];

  const [activeTab, setActiveTab] = useState(0);

  return (
    <AnimatePresence>
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-800 bg-opacity-35 flex items-center justify-center p-2 sm:p-4 z-9999">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
              duration: 0.5,
            }}
            className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] overflow-y-auto"
          >
            <div className="bg-primary text-white flex justify-between items-center p-4 sm:p-4 ">
              <h2 className="text-2xl font-semibold uppercase">
                {initialData ? "Edit Foundry Log" : "New Foundry Log"}
              </h2>
              <Button
                icon={IoClose}
                iconColor="text-white"
                iconSize="text-2xl"
                onClick={onCancel}
              />
              {/* {searchQuery && (
                <Button
                  icon={MdClose}
                  onClick={() => setSearchQuery("")}
                  iconSize="w-5 h-5"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-danger"
                ></Button>
              )} */}
            </div>

            <div className="flex flex-wrap border-b border-slate-300 m-4">
              {tabs.map((tab, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveTab(idx)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-all duration-300 ${
                    activeTab === idx
                      ? "border-primary text-primary"
                      : "border-transparent text-slate-500 hover:text-primary"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 p-4 sm:p-6 ">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-primary border-b py-4">
                <InputField
                  label="Date"
                  type="date"
                  required
                  value={foundryEntry.CASTING_DATE}
                  onChange={(val) =>
                    setFoundryEntry({ ...foundryEntry, CASTING_DATE: val })
                  }
                />
                <InputField
                  label="Casting No"
                  value={foundryEntry.CASTING_NO}
                  onChange={() => {}}
                  readOnly
                  disabled
                />

                <InputField
                  type="text"
                  label="In-Charge Person"
                  placeholder="Full Name"
                  value={foundryEntry.IN_CHARGE_ID}
                  onChange={(val) =>
                    setFoundryEntry({ ...foundryEntry, IN_CHARGE_ID: val })
                  }
                  required
                />
              </div>

              {activeTab === 0 && (
                <>
                  {/* Scrap Inputs */}
                  <SectionTitle title="Scrap Inputs" />
                  <div className="space-y-3">
                    {scrapInputs.map((scrap, index) => (
                      <div
                        key={index}
                        className="p-4 border border-slate-200 rounded-md space-y-4 mb-4 relative"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <InputField
                            type="select"
                            label={`SKU for Scrap ${index + 1}`}
                            value={scrap.Inward_No}
                            onChange={(val) =>
                              handleScrapChange(index, "Inward_No", val)
                            }
                            options={isSkuData}
                            required
                          />

                          <InputField
                            label="Quantity Used"
                            type="text"
                            value={scrap.QUANTITY}
                            onChange={(val) =>
                              handleScrapChange(index, "QUANTITY", val)
                            }
                            unit="Kg"
                            placeholder="e.g., 500"
                            required
                          />
                        </div>
                        <Button
                          icon={HiMinusCircle}
                          onClick={() => handleRemoveScrapInput(index)}
                          className="absolute top-0 right-2 text-red-500 hover:text-red-700 text-sm"
                        ></Button>
                        {scrap.skuDetail && (
                          <div className="text-xs text-slate-600 mt-1 bg-slate-100 px-3 py-2 rounded">
                            Selected SKU details: {scrap.skuDetail}
                          </div>
                        )}
                      </div>
                    ))}

                    <Button
                      text="+ Add Scrap Item"
                      onClick={handleAddScrapInput}
                      className="bg-primary/80 p-2 text-sm text-white rounded-md tracking-wider font-semibold "
                    ></Button>

                    <InputField
                      label="Scrap Total"
                      type="text"
                      value={scrapTotal.toFixed(2)}
                      readOnly
                      unit="Kg"
                      className="text-right"
                      disabled
                    />
                  </div>
                </>
              )}

              {activeTab === 1 && (
                <>
                  {" "}
                  {/* LDO Table */}
                  <SectionTitle title="LDO Table" />
                  <div className="space-y-3">
                    {[foundryEntry].map((entry, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
                      >
                        <InputField
                          label="Pre Heating Level"
                          unit="Inches"
                          value={entry.LDO_PRE_HEATING_LEVEL}
                          onChange={(val) =>
                            setFoundryEntry({
                              ...entry,
                              LDO_PRE_HEATING_LEVEL: val,
                            })
                          }
                        />
                        <InputField
                          label="Charging Start Level"
                          unit="Inches"
                          value={entry.LDO_CHARGING_START_LEVEL}
                          onChange={(val) =>
                            setFoundryEntry({
                              ...entry,
                              LDO_CHARGING_START_LEVEL: val,
                            })
                          }
                        />
                        <InputField
                          label="Casting Time Level"
                          unit="Inches"
                          value={entry.LDO_CATING_TIME_LEVEL}
                          onChange={(val) =>
                            setFoundryEntry({
                              ...entry,
                              LDO_CATING_TIME_LEVEL: val,
                            })
                          }
                        />
                        <InputField
                          label="Main Tank Opening Level"
                          unit="Inches"
                          value={entry.LDO_MAIN_TANK_OPENING_LEVEL}
                          onChange={(val) =>
                            setFoundryEntry({
                              ...entry,
                              LDO_MAIN_TANK_OPENING_LEVEL: val,
                            })
                          }
                        />
                        <InputField
                          label="Main Tank Closing Level"
                          unit="Inches"
                          value={entry.LDO_MAIN_TANK_CLOSING_LEVEL}
                          onChange={(val) =>
                            setFoundryEntry({
                              ...entry,
                              LDO_MAIN_TANK_CLOSING_LEVEL: val,
                            })
                          }
                        />
                        <InputField
                          label="Total Consumption"
                          unit="Ltr"
                          value={entry.LDO_TOTAL_CONSUMPTION}
                          onChange={(val) =>
                            setFoundryEntry({
                              ...entry,
                              LDO_TOTAL_CONSUMPTION: val,
                            })
                          }
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}

              {activeTab === 2 && (
                <>
                  {/* Other Metal Inputs */}
                  <SectionTitle title="Other Metal Inputs" />
                  <div className="space-y-3">
                    {otherMetalEntries.map((item, index) => (
                      <div
                        key={index}
                        className=" grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
                      >
                        <InputField
                          label="Ingot"
                          placeholder="Quantity"
                          unit="Kg"
                          value={item.INGOT}
                          onChange={(val) =>
                            handleOtherMetalChange(index, "INGOT", val)
                          }
                        />
                        <InputField
                          label="Plant Scrap"
                          placeholder="Quantity"
                          unit="Kg"
                          value={item.PLANT_SCRAP}
                          onChange={(val) =>
                            handleOtherMetalChange(index, "PLANT_SCRAP", val)
                          }
                        />
                        <InputField
                          label="Plant Discard"
                          placeholder="Quantity"
                          unit="Kg"
                          value={item.PLANT_DISCARD}
                          onChange={(val) =>
                            handleOtherMetalChange(index, "PLANT_DISCARD", val)
                          }
                        />
                        <InputField
                          label="Dross Ingot"
                          placeholder="Quantity"
                          unit="Kg"
                          value={item.DROSS_INGOT}
                          onChange={(val) =>
                            handleOtherMetalChange(index, "DROSS_INGOT", val)
                          }
                        />
                        <InputField
                          label="Bend Log"
                          placeholder="Quantity"
                          unit="Kg"
                          value={item.BEND_LOG}
                          onChange={(val) =>
                            handleOtherMetalChange(index, "BEND_LOG", val)
                          }
                        />
                        <InputField
                          label="Hill Metal"
                          placeholder="Quantity"
                          unit="Kg"
                          value={item.HILL_METAL}
                          onChange={(val) =>
                            handleOtherMetalChange(index, "HILL_METAL", val)
                          }
                        />
                        <InputField
                          label="Pit Metal"
                          placeholder="Quantity"
                          unit="Kg"
                          value={item.PIT_METAL}
                          onChange={(val) =>
                            handleOtherMetalChange(index, "PIT_METAL", val)
                          }
                        />
                        <InputField
                          label="Tendis Metal"
                          placeholder="Quantity"
                          unit="Kg"
                          value={item.TENDIS_METAL}
                          onChange={(val) =>
                            handleOtherMetalChange(index, "TENDIS_METAL", val)
                          }
                        />
                        <InputField
                          label="Dross Metal"
                          placeholder="Quantity"
                          unit="Kg"
                          value={item.DROSS_METAL}
                          onChange={(val) =>
                            handleOtherMetalChange(index, "DROSS_METAL", val)
                          }
                        />
                      </div>
                    ))}

                    <InputField
                      label="Total Input (Scrap + Other Metals)"
                      type="text"
                      value={totalInputKg.toFixed(2)}
                      readOnly
                      unit="Kg"
                      className="text-right"
                      disabled
                    />
                  </div>
                </>
              )}

              {activeTab === 3 && (
                <>
                  {/* Furnace Operating Time */}
                  <SectionTitle title="Furnace Operating Time" />
                  <div className="space-y-3">
                    {furnaceEntries.map((entry, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
                      >
                        <InputField
                          label="Furnace No."
                          placeholder="e.g., F-01"
                          value={entry.FURNACE_NO}
                          onChange={(val) =>
                            handleFurnaceChange(index, "FURNACE_NO", val)
                          }
                          disabled
                        />
                        <div className="flex items-end pt-5">
                          <label className="flex items-center text-sm font-medium text-slate-700">
                            <input
                              type="checkbox"
                              className="mr-2"
                              checked={entry.PRE_HEATED || false}
                              onChange={(e) =>
                                handleFurnaceChange(
                                  index,
                                  "PRE_HEATED",
                                  e.target.checked
                                )
                              }
                            />
                            Furnace Pre-heated?
                          </label>
                        </div>
                        <InputField
                          label="Fur- Charging Start Time"
                          type="datetime-local"
                          value={entry.CHARGING_START_TIME}
                          onChange={(val) =>
                            handleFurnaceChange(
                              index,
                              "CHARGING_START_TIME",
                              val
                            )
                          }
                        />
                        <InputField
                          label="Fur- Charging End Time"
                          type="datetime-local"
                          value={entry.CHARGING_END_TIME}
                          onChange={(val) =>
                            handleFurnaceChange(index, "CHARGING_END_TIME", val)
                          }
                        />
                        <InputField
                          label="Casting Start Time"
                          type="datetime-local"
                          value={entry.CASTING_START_TIME}
                          onChange={(val) =>
                            handleFurnaceChange(
                              index,
                              "CASTING_START_TIME",
                              val
                            )
                          }
                        />
                        <InputField
                          label="Casting End Time"
                          type="datetime-local"
                          value={entry.CASTING_END_TIME}
                          onChange={(val) =>
                            handleFurnaceChange(index, "CASTING_END_TIME", val)
                          }
                        />
                        <InputField
                          label="Total Time to Produce"
                          readOnly
                          value={entry.TOTAL_TIME}
                          disabled
                          placeholder="End before start"
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}

              {activeTab === 4 && (
                <>
                  {/* consumable */}
                  <SectionTitle title="consumable" />
                  <div className="space-y-3">
                    {chemicalEntries.map((item, index) => (
                      <div
                        key={index}
                        className="p-4 border border-slate-200 rounded-md relative grid grid-cols-1 md:grid-cols-3 gap-4 mb-4"
                      >
                        <InputField
                          type="selectWithAdd"
                          label="Raw Material"
                          value={item.MATERIAL_ID}
                          onChange={(selected) => {
                            const selectedMaterial = rawMaterialsResData.find(
                              (rm) => rm.id === selected?.value
                            );
                            const updated = [...chemicalEntries];
                            updated[index].MATERIAL_ID = selected?.value;
                            updated[index].stock =
                              selectedMaterial?.totalQuantity || 0;
                            setChemicalEntries(updated);
                          }}
                          options={isRawMaterialData}
                          onAddNewClick={() => setModalType("rawMaterial")}
                        />
                        <InputField
                          label="Quantity Used"
                          type="text"
                          value={item.qty}
                          onChange={(val) => {
                            const updated = [...chemicalEntries];
                            updated[index].qty = val;
                            const selectedMaterial = rawMaterialsResData.find(
                              (rm) =>
                                rm.id === Number(updated[index].MATERIAL_ID)
                            );
                            const originalStock =
                              selectedMaterial?.totalQuantity || 0;
                            const remaining = Math.max(
                              originalStock - Number(val || 0),
                              0
                            );

                            updated[index].stock = remaining;
                            setChemicalEntries(updated);
                          }}
                          unit="Kg"
                        />

                        <InputField
                          label="Available Stock"
                          type="text"
                          value={item.stock}
                          onChange={(val) => {
                            const updated = [...chemicalEntries];
                            updated[index].qty = val;
                            updated[index].stock = Math.max(
                              (updated[index].stock || 0) - Number(val),
                              0
                            );
                            setChemicalEntries(updated);
                          }}
                          unit="Kg"
                        />
                        <Button
                          icon={HiMinusCircle}
                          onClick={() => handleRemoveChemicals(index)}
                          className="absolute top-0 right-2 text-red-500 hover:text-red-700 text-sm"
                        ></Button>
                      </div>
                    ))}
                    <div className="text-right mb-4">
                      <Button
                        text="+ Add Chemicals Used Item"
                        onClick={handleAddChemicalInput}
                        className="text-sm text-primary hover:text-sky-800"
                      ></Button>
                    </div>
                  </div>
                </>
              )}

              {activeTab === 5 && (
                <>
                  {/* By-Products Generated */}
                  <SectionTitle title="By-Products Generated" />
                  <div className="space-y-3">
                    {byProductEntries.map((item, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
                      >
                        <InputField
                          label="Dross"
                          unit="Kg"
                          placeholder="Weight"
                          value={item.DROSS}
                          onChange={(val) =>
                            setByProductEntries((prev) => {
                              const updated = [...prev];
                              updated[index].DROSS = val;
                              return updated;
                            })
                          }
                        />
                        <InputField
                          label="Aluminum Metal (Reusable)"
                          unit="Kg"
                          placeholder="Weight"
                          value={item.ALUMINIUM_METAL_REUSABLE}
                          onChange={(val) =>
                            setByProductEntries((prev) => {
                              const updated = [...prev];
                              updated[index].ALUMINIUM_METAL_REUSABLE = val;
                              return updated;
                            })
                          }
                        />
                        <InputField
                          label="Iron"
                          unit="Kg"
                          placeholder="Weight"
                          value={item.IRON}
                          onChange={(val) =>
                            setByProductEntries((prev) => {
                              const updated = [...prev];
                              updated[index].IRON = val;
                              return updated;
                            })
                          }
                        />
                        <InputField
                          label="Dross Ingot"
                          unit="Kg"
                          placeholder="Weight"
                          value={item.DROSS_INGOT}
                          onChange={(val) =>
                            setByProductEntries((prev) => {
                              const updated = [...prev];
                              updated[index].DROSS_INGOT = val;
                              return updated;
                            })
                          }
                        />
                        <InputField
                          label="Burning Loss"
                          unit="Kg"
                          placeholder="Weight"
                          value={item.BURNING_LOSS}
                          onChange={(val) =>
                            setByProductEntries((prev) => {
                              const updated = [...prev];
                              updated[index].BURNING_LOSS = val;
                              return updated;
                            })
                          }
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}

              {activeTab === 6 && (
                <>
                  {/* Log Output Data */}
                  <SectionTitle title="Log Output Data" />
                  <div className="space-y-3">
                    {[foundryEntry].map((entry, index) => (
                      <>
                        <div
                          key={index}
                          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
                        >
                          <InputField
                            label="No of Logs Output"
                            unit="pcs"
                            value={entry.NO_OF_LOGS}
                            onChange={(val) =>
                              setFoundryEntry((prev) => ({
                                ...prev,
                                NO_OF_LOGS: val,
                              }))
                            }
                          />
                          <InputField
                            label="Length of Log"
                            unit="mm"
                            value={entry.LOG_LENGTH}
                            onChange={(val) =>
                              setFoundryEntry((prev) => ({
                                ...prev,
                                LOG_LENGTH: val,
                              }))
                            }
                          />
                          <InputField
                            label="One Log Weight (L*0.034)"
                            unit="Kg"
                            value={entry.LOG_LENGTH * (0.034).toFixed(2)}
                            readOnly
                            disabled
                          />
                        </div>
                        <InputField
                          label="Total Output Wt (Formula L*N*0.034)"
                          unit="Kg"
                          value={
                            entry.NO_OF_LOGS *
                            entry.LOG_LENGTH *
                            (0.034).toFixed(2)
                          }
                          readOnly
                          disabled
                        />
                      </>
                    ))}
                  </div>
                </>
              )}

              {activeTab === 7 && (
                <>
                  {/* Alloy & Spectro Analysis */}
                  <SectionTitle title="Alloy & Spectro Analysis" />
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <InputField
                        label="Alloy Grade"
                        type="text"
                        placeholder="e.g., A+"
                        value={foundryEntry.ALLOY_GRAD}
                        onChange={(val) =>
                          setFoundryEntry((prev) => ({
                            ...prev,
                            ALLOY_GRAD: val,
                          }))
                        }
                        required
                      />
                      <InputField
                        label="H-Line Grade"
                        type="text"
                        placeholder="e.g., A+"
                        value={foundryEntry.HLINE_GRAD}
                        onChange={(val) =>
                          setFoundryEntry((prev) => ({
                            ...prev,
                            HLINE_GRAD: val,
                          }))
                        }
                        required
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <h2 className="text-base font-semibold  mb-2">
                          Sample Spectro Report Component (%)
                        </h2>
                        {sampleSpectroInputs.map((item, index) => (
                          <div
                            key={index}
                            className="p-4 border border-slate-200 rounded-md relative grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3"
                          >
                            <Button
                              icon={HiMinusCircle}
                              onClick={() => handleRemoveSampleSpectro(index)}
                              className="absolute top-2 right-2 text-red-500 hover:text-red-700 text-lg"
                              title="Remove"
                            ></Button>
                            <InputField
                              type="selectWithAdd"
                              label="Select Component"
                              value={item.componentId}
                              onChange={(selected) => {
                                const updated = [...sampleSpectroInputs];
                                updated[index].componentId = selected?.value;
                                setSampleSpectroInputs(updated);
                              }}
                              options={isSpectroData}
                              onAddNewClick={() =>
                                setModalType("sampleSpectroComponent")
                              }
                            />
                            <InputField
                              label="Weight"
                              type="text"
                              value={item.percentage}
                              onChange={(val) => {
                                const updated = [...sampleSpectroInputs];
                                updated[index].percentage = val;
                                setSampleSpectroInputs(updated);
                              }}
                              unit="%"
                              placeholder="e.g.,0.5"
                            />
                          </div>
                        ))}

                        <div className=" mb-6">
                          <Button
                            text=" + Add Sample Spectro Report"
                            onClick={handleAddSampleSpectro}
                            className="text-sm text-primary hover:text-sky-800 font-semibold"
                          ></Button>
                        </div>
                      </div>
                      <div>
                        <h2 className="text-base font-semibold mb-2">
                          Final Spectro Report Component (%)
                        </h2>
                        {finalSpectroInputs.map((item, index) => (
                          <div
                            key={index}
                            className="p-4 border border-slate-200 rounded-md relative grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3"
                          >
                            <Button
                              icon={HiMinusCircle}
                              onClick={() => handleRemoveFinalSpectro(index)}
                              className="absolute top-2 right-2 text-red-500 hover:text-red-700 text-lg"
                              title="Remove"
                            ></Button>
                            <InputField
                              type="selectWithAdd"
                              label="Select Component"
                              value={item.componentId}
                              onChange={(selected) => {
                                const updated = [...finalSpectroInputs];
                                updated[index].componentId = selected?.value;
                                setFinalSpectroInputs(updated);
                              }}
                              options={isSpectroData}
                              onAddNewClick={() =>
                                setModalType("finalSpectroComponent")
                              }
                            />
                            <InputField
                              label="Weight"
                              type="text"
                              value={item.percentage}
                              onChange={(val) => {
                                const updated = [...finalSpectroInputs];
                                updated[index].percentage = val;
                                setFinalSpectroInputs(updated);
                              }}
                              unit="%"
                              placeholder="e.g.,4.8"
                            />
                          </div>
                        ))}

                        <div className=" mb-6">
                          <Button
                            text=" + Add Final Spectro Report"
                            onClick={handleAddFinalSpectro}
                            className="text-sm text-primary hover:text-sky-800 font-semibold"
                          ></Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="border-primary border-t py-4">
                <label
                  htmlFor="REMARK"
                  className="block text-sm font-medium text-slate-700 uppercase mb-2"
                >
                  Remarks (Optional)
                </label>
                <InputField
                  type="textarea"
                  name="REMARK"
                  rows={3}
                  placeholder="Enter any observations or notes..."
                  className="mt-1 block w-full p-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  value={foundryEntry.REMARK}
                  onChange={(val) =>
                    setFoundryEntry((prev) => ({
                      ...prev,
                      REMARK: val,
                    }))
                  }
                />
              </div>

              <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-5 border-t border-slate-200 mt-6">
                <Button
                  text="Cancel"
                  className="text-primary text-md font-semibold p-2 rounded-md bg-gray-100 border border-primary"
                  icon={IoMdClose}
                  onClick={onCancel}
                  iconSpacing="pr-2"
                />
                <Button
                  text="Save and Continue Later"
                  className="text-black text-md font-semibold p-2 rounded-md bg-orange/50 border border-orange/50"
                  icon={BsSave2}
                  iconColor="text-black"
                  iconSpacing="pr-2"
                  onClick={handleSubmit}
                />
                <Button
                  text="Finish and Save Log"
                  className="text-black text-md font-semibold p-2 rounded-md bg-orange"
                  icon={FaPaperPlane}
                  iconColor="text-black"
                  iconSpacing="pr-2"
                />
              </div>
            </form>
            <Model
              isOpen={!!modalType}
              onClose={() => setModalType(false)}
              name={`Add ${modalType}`}
            >
              <EntityAddModal
                type={modalType}
                onClose={(newOption) => {
                  setModalType(null);
                  handleEntityAddSuccess(modalType, newOption);
                }}
              />
            </Model>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default FoundryForm;
